// 위젯 전역에서 사용하는 제품 이미지 placeholder/normalize 유틸.
// AiTopPicksCard / CompareTableCard / ProductDetailCard / PromotionListCard 등에서
// 공통으로 사용하여 "엑박" (broken image) 가 노출되지 않도록 한다.

import type { SyntheticEvent } from "react";

const _PLACEHOLDER_LABEL = "Image";
const _PLACEHOLDER_BG = "#c8c8c8";
const _PLACEHOLDER_FG = "#333";
const _PLACEHOLDER_SIZE = 350;

/**
 * 절대 URL / protocol-relative / www-prefixed 케이스를 일관된 https URL 로 변환한다.
 * 빈 문자열이거나 normalize 대상이 아니면 입력값을 그대로 반환한다.
 */
export function normalizeProductImage(url?: string | null): string {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("www.")) return `https://${url}`;
  return url;
}

/**
 * 제품 이미지가 없거나 onError 로 실패했을 때 표시하는 inline SVG placeholder.
 * 시안 톤 (#c8c8c8 / #333) 을 재현한다.
 */
export function fallbackProductImage(): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${_PLACEHOLDER_SIZE}" height="${_PLACEHOLDER_SIZE}" viewBox="0 0 ${_PLACEHOLDER_SIZE} ${_PLACEHOLDER_SIZE}" role="img" aria-label="LG product placeholder">
      <rect width="${_PLACEHOLDER_SIZE}" height="${_PLACEHOLDER_SIZE}" fill="${_PLACEHOLDER_BG}"/>
      <text x="${_PLACEHOLDER_SIZE / 2}" y="${_PLACEHOLDER_SIZE / 2}" text-anchor="middle" alignment-baseline="middle"
            fill="${_PLACEHOLDER_FG}" font-size="20" font-family="sans-serif">${_PLACEHOLDER_LABEL}</text>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/**
 * `<img onError>` 에서 사용할 핸들러. 무한 루프를 막기 위해 onerror 를 즉시 해제한 뒤
 * placeholder src 로 교체한다.
 */
export function handleProductImageError(
  event: SyntheticEvent<HTMLImageElement>
): void {
  const img = event.currentTarget;
  img.onerror = null;
  img.src = fallbackProductImage();
}

/**
 * 입력 src 가 비어있으면 placeholder 를, 있으면 normalize 된 URL 을 반환한다.
 * `<img src={resolveProductImageSrc(item.image)} onError={handleProductImageError} />` 패턴으로 사용.
 */
export function resolveProductImageSrc(url?: string | null): string {
  const normalized = normalizeProductImage(url);
  return normalized || fallbackProductImage();
}
